<template>
  <section class="officials-slots-section" aria-label="Match officials slots">
    <div class="card officials-slots-card">
      <div class="card-header">
        <div class="card-title">
          <i class="fa-solid fa-user-shield" aria-hidden="true"></i>
          <h2>Officials slots</h2>
        </div>
      </div>
      <div class="card-body">
        <p class="officials-slots-lead">
          Upcoming matches that requested registered officials. Open slots are filled when
          officials claim them from the officiating pool.
        </p>

        <div v-if="matches.length > 0" class="officials-match-list">
          <article
            v-for="match in matches"
            :key="match.id || match.match_id"
            class="officials-match-row"
          >
            <div class="officials-match-row__head">
              <div class="officials-match-row__title">
                <span class="officials-match-row__id">Match #{{ match.id || match.match_id }}</span>
                <span class="officials-match-row__vs">vs {{ opponentLabel(match) }}</span>
              </div>
              <div class="officials-match-row__meta">
                <span v-if="matchDate(match)" class="officials-match-row__when">
                  <i class="fa-solid fa-calendar-day" aria-hidden="true"></i>
                  {{ formatDate(matchDate(match)) }}
                  <span v-if="formatTime">· {{ formatTime(matchDate(match)) }}</span>
                </span>
                <span
                  class="officials-pool-badge"
                  :class="poolBadgeClass(match)"
                >
                  {{ poolBadgeLabel(match) }}
                </span>
              </div>
            </div>

            <p v-if="!isPoolOpen(match)" class="officials-match-row__hint">
              Slots open in the officiating pool after your opponent accepts this challenge.
            </p>

            <ul
              v-else
              class="officials-slot-chips"
              :aria-label="`Official slots for match ${match.id || match.match_id}`"
            >
              <li
                v-for="slot in slotsForMatch(match)"
                :key="`${match.id}-${slot.slot}`"
                class="officials-slot-chip"
                :class="slot.status === 'filled' ? 'officials-slot-chip--taken' : 'officials-slot-chip--open'"
                :title="slot.role"
              >
                <i :class="slotIcon(slot)" aria-hidden="true"></i>
                <span class="officials-slot-chip__label">{{ slot.label }}</span>
                <span class="officials-slot-chip__state">
                  <template v-if="slot.status === 'filled'">
                    {{ slot.official_name || 'Assigned' }}
                  </template>
                  <template v-else>Open</template>
                </span>
              </li>
            </ul>

            <div
              v-if="isPoolOpen(match) && slotsForMatch(match).length"
              class="officials-match-row__summary"
            >
              <span>
                <strong>{{ filledCount(match) }}</strong> taken ·
                <strong>{{ openCount(match) }}</strong> open
              </span>
            </div>
          </article>
        </div>

        <div v-else class="empty-state officials-slots-empty">
          <div class="empty-icon">
            <i class="fa-solid fa-user-check" aria-hidden="true"></i>
          </div>
          <h3>No officiated matches yet</h3>
          <p>
            When you book officials on a challenge, upcoming matches will show slot status here.
          </p>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { activeOfficiatingSlots, OFFICIATING_SLOTS } from "@/routes/match_hub/football/officiatingConstants.js";

export default {
  name: "MatchOfficialsSlots",
  props: {
    matches: {
      type: Array,
      default: () => [],
    },
    teamId: {
      type: [Number, String],
      default: null,
    },
    formatDate: {
      type: Function,
      required: true,
    },
    formatTime: {
      type: Function,
      default: null,
    },
  },
  methods: {
    matchDate(match) {
      return match?.date || match?.scheduled_at || null;
    },

    opponentLabel(match) {
      const tid = Number(this.teamId);
      const homeId = Number(match?.home_team_id ?? match?.team_id);
      if (tid && homeId === tid) {
        return match?.opponent_name || "Opponent";
      }
      return match?.opponent_name || match?.challenger_team_name || "Opponent";
    },

    isPoolOpen(match) {
      const status = String(match?.status || "").toLowerCase();
      return ["confirmed", "scheduled"].includes(status);
    },

    poolBadgeLabel(match) {
      if (!this.isPoolOpen(match)) return "Awaiting acceptance";
      const open = this.openCount(match);
      if (open === 0) return "All slots taken";
      return `${open} open in pool`;
    },

    poolBadgeClass(match) {
      if (!this.isPoolOpen(match)) return "officials-pool-badge--pending";
      if (this.openCount(match) === 0) return "officials-pool-badge--full";
      return "officials-pool-badge--open";
    },

    slotsForMatch(match) {
      if (Array.isArray(match.officiating_slots) && match.officiating_slots.length) {
        return match.officiating_slots;
      }
      const count = Number(match.officiating_count || match.officiating_slots_requested || 0);
      if (!count) return [];
      const defs = activeOfficiatingSlots(count);
      return defs.map((d) => ({
        slot: d.slot,
        label: d.label,
        role: d.role,
        status: "open",
        official_name: null,
      }));
    },

    filledCount(match) {
      if (typeof match.officiating_slots_filled === "number") {
        return match.officiating_slots_filled;
      }
      return this.slotsForMatch(match).filter((s) => s.status === "filled").length;
    },

    openCount(match) {
      if (typeof match.officiating_slots_open === "number") {
        return match.officiating_slots_open;
      }
      return this.slotsForMatch(match).filter((s) => s.status === "open").length;
    },

    slotIcon(slot) {
      if (slot.status === "filled") return "fa-solid fa-circle-check";
      const def = OFFICIATING_SLOTS.find((s) => s.slot === slot.slot);
      return def?.icon || "fa-solid fa-user-shield";
    },
  },
};
</script>

<style scoped>
.officials-slots-section {
  margin-top: 20px;
}

.officials-slots-card {
  border: 1px solid var(--border-color-hover1, rgba(255, 255, 255, 0.08));
}

.officials-slots-lead {
  margin: 0 0 14px;
  font-size: 0.82rem;
  line-height: 1.5;
  color: var(--text-color2, var(--subtext-color));
}

.officials-match-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.officials-match-row {
  padding: 12px 14px;
  border-radius: 12px;
  border: 1px solid var(--border-color-hover1, rgba(255, 255, 255, 0.08));
  background: var(--background-color2, var(--meta-bg));
}

.officials-match-row__head {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 10px;
}

.officials-match-row__title {
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 0.88rem;
}

.officials-match-row__id {
  font-weight: 700;
  color: var(--text-color1, var(--heading-color));
}

.officials-match-row__vs {
  font-size: 0.8rem;
  color: var(--text-color2, var(--subtext-color));
}

.officials-match-row__meta {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
  font-size: 0.78rem;
  color: var(--text-color2, var(--subtext-color));
}

.officials-match-row__when i {
  margin-right: 4px;
  opacity: 0.85;
}

.officials-pool-badge {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.02em;
}

.officials-pool-badge--pending {
  background: rgba(234, 179, 8, 0.15);
  color: #ca8a04;
  border: 1px solid rgba(234, 179, 8, 0.35);
}

.officials-pool-badge--open {
  background: rgba(34, 197, 94, 0.12);
  color: #16a34a;
  border: 1px solid rgba(34, 197, 94, 0.3);
}

.officials-pool-badge--full {
  background: rgba(59, 130, 246, 0.12);
  color: #2563eb;
  border: 1px solid rgba(59, 130, 246, 0.3);
}

.officials-match-row__hint {
  margin: 0;
  font-size: 0.78rem;
  line-height: 1.45;
  color: var(--text-color2, var(--subtext-color));
  font-style: italic;
}

.officials-slot-chips {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.officials-slot-chip {
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  min-width: 0;
  flex: 1 1 120px;
  max-width: 100%;
  padding: 8px 10px;
  border-radius: 10px;
  font-size: 0.72rem;
  border: 1px solid transparent;
}

.officials-slot-chip i {
  font-size: 0.85rem;
  margin-bottom: 2px;
}

.officials-slot-chip__label {
  font-weight: 700;
  font-size: 0.74rem;
}

.officials-slot-chip__state {
  font-size: 0.7rem;
  opacity: 0.9;
  word-break: break-word;
}

.officials-slot-chip--open {
  background: var(--background-color1, var(--card-bg));
  border-color: rgba(34, 197, 94, 0.35);
  color: var(--text-color1, var(--heading-color));
}

.officials-slot-chip--open i {
  color: #16a34a;
}

.officials-slot-chip--taken {
  background: var(--background-color1, var(--card-bg));
  border-color: var(--border-color-hover, rgba(58, 134, 255, 0.4));
  color: var(--text-color1, var(--heading-color));
}

.officials-slot-chip--taken i {
  color: var(--border-color-hover, #3a86ff);
}

.officials-match-row__summary {
  margin-top: 8px;
  font-size: 0.74rem;
  color: var(--text-color2, var(--subtext-color));
}

.officials-match-row__summary strong {
  color: var(--text-color1, var(--heading-color));
}

.officials-slots-empty {
  padding: 24px 12px;
}

.officials-slots-empty h3 {
  font-size: 1rem;
  margin-bottom: 6px;
}

.officials-slots-empty p {
  font-size: 0.85rem;
  max-width: 36ch;
  margin: 0 auto;
}
</style>
